import {
  computed,
  effect,
  inject,
  Injectable,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { filter, finalize } from 'rxjs/operators';

import { Product } from '../models/product.model';
import { StockAlertService } from './stock-alert.service';
import { StockAlertStore } from './stock-alert.store';
import { ToastService } from './toast.service';
import { StockAlertError, StockAlertResponse } from '../models/stock-alert.model';

interface StockAlertModalState {
  open: boolean;
  product: Product | null;
}

/**
 * Estado global del modal de "Avisarme cuando haya stock", análogo a
 * `PrepurchaseModalService`. El `StockAlertModalComponent` (montado una única
 * vez en app.ts) lee `state()`; los surfaces (ProductCard via cada host:
 * catálogo, home, PDP quickviews, wishlist, search) invocan `open(product)`
 * cuando el usuario pide alerta y `cancel(product)` cuando la quita.
 *
 * Bloquea el scroll del body mientras el modal está abierto y cierra
 * automáticamente en cada NavigationEnd.
 */
@Injectable({ providedIn: 'root' })
export class StockAlertModalService {
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly router = inject(Router);
  private readonly stockAlertService = inject(StockAlertService);
  private readonly stockAlertStore = inject(StockAlertStore);
  private readonly toastService = inject(ToastService);

  private readonly _state = signal<StockAlertModalState>({
    open: false,
    product: null,
  });

  readonly state = this._state.asReadonly();
  readonly isOpen = computed(() => this._state().open);
  readonly product = computed(() => this._state().product);

  /** Productos con un alta en vuelo (ficha o modal). Ver `requestAlert()`. */
  private readonly _altasInFlight = signal<ReadonlySet<number>>(new Set());

  constructor() {
    effect(() => {
      if (!this.isBrowser) return;
      document.body.style.overflow = this._state().open ? 'hidden' : '';
    });

    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => this.close());
  }

  open(product: Product): void {
    this._state.set({ open: true, product });
  }

  close(): void {
    this._state.update((s) => ({ ...s, open: false }));
  }

  /**
   * ¿Hay un alta en vuelo para este producto? Lo leen la ficha y el modal para
   * deshabilitar su «Avisarme» mientras tanto (es un signal: reactivo en
   * `computed` y plantillas).
   */
  isSubscribing(productId: number | string | null | undefined): boolean {
    return productId != null && this._altasInFlight().has(Number(productId));
  }

  /**
   * Alta del aviso de stock, ÚNICA para ficha y modal. Un alta en vuelo por
   * producto: si ya hay una, devuelve `false` y no sale ningún POST (la UI que
   * lo pidió sigue en «enviando» vía `isSubscribing`). Sin esto, ficha y modal
   * podían lanzar dos altas del mismo producto y la respuesta más antigua
   * pisaba en el store el token de la posterior (Astra W3, H-D2).
   *
   * El servicio es `root` y se suscribe él mismo: el POST no se aborta aunque
   * se destruya la vista que lo pidió. El token va al store SIEMPRE, antes de
   * los handlers, que solo aplican efectos de vista (y deciden ellos si su
   * vista sigue vigente). El candado se libera en `finalize`: tras éxito,
   * error o 404.
   */
  requestAlert(
    productId: number,
    email: string,
    handlers: {
      onSuccess?: (res: StockAlertResponse) => void;
      onError?: (err: unknown) => void;
    } = {},
  ): boolean {
    if (this.isSubscribing(productId)) return false;
    this._altasInFlight.update((ids) => new Set(ids).add(productId));

    this.stockAlertService
      .createAlert({ product_id: productId, email })
      .pipe(
        finalize(() =>
          this._altasInFlight.update((ids) => {
            const next = new Set(ids);
            next.delete(productId);
            return next;
          }),
        ),
      )
      .subscribe({
        next: (res) => {
          this.stockAlertStore.save({
            token: res.token,
            email: res.email,
            product_id: productId,
            subscribed_at: new Date().toISOString(),
          });
          handlers.onSuccess?.(res);
        },
        error: (err: unknown) => handlers.onError?.(err),
      });
    return true;
  }

  /**
   * Cancelación one-click desde una card. Lee el token guardado en el store,
   * pega DELETE al backend y borra del localStorage ESE token (no todo lo del
   * producto). Si el server devuelve 404 (token desconocido) lo borramos
   * igualmente: el estado local estaba desincronizado y el resultado neto
   * deseado es "no avisarme" → cumplido.
   *
   * `onCancelled` se llama SOLO cuando la cancelación queda confirmada (200 o
   * 404 `token_not_found`), nunca al pulsar ni si falla la red. Lo usa la PDP
   * para reiniciar su formulario y dejar de fiarse del `has_alert` que trajo
   * el producto; el resto de superficies no lo necesitan: leen el store.
   */
  cancel(product: Product, onCancelled?: () => void): void {
    const productId = Number(product.id);
    if (!Number.isFinite(productId)) return;

    const stored = this.stockAlertStore.getByProductId(productId);
    if (!stored) {
      // Sin token local no podemos pegarle al backend — limpiamos por si
      // quedó algún rastro y seguimos.
      this.stockAlertStore.removeByProductId(productId);
      return;
    }

    // El token que se cancela, capturado AHORA. Al resolver se borra solo ese:
    // si mientras tanto se dio de alta un aviso nuevo para el mismo producto,
    // su token no es el cancelado y se conserva. No se relee del store.
    const cancelledToken = stored.token;
    this.stockAlertService.unsubscribe(cancelledToken).subscribe({
      next: () => {
        this.stockAlertStore.removeByToken(cancelledToken);
        onCancelled?.();
        this.toastService.show({
          type: 'success',
          message: 'Ya no recibirás avisos de este producto.',
        });
      },
      error: (err) => {
        if (err instanceof StockAlertError && err.reason === 'token_not_found') {
          this.stockAlertStore.removeByToken(cancelledToken);
          onCancelled?.();
          this.toastService.show({
            type: 'info',
            message: 'La alerta ya estaba cancelada.',
          });
          return;
        }
        this.toastService.show({
          type: 'error',
          message: 'No pudimos cancelar la alerta. Intenta de nuevo.',
        });
      },
    });
  }
}
